import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCoins, FaCheckCircle } from 'react-icons/fa';
import Button from '../misc/Button';
import Icon from '../icons/Icon';
import { PayerType, PaymentOrderDetailsDto } from '../../types/payments.model';
import {
  getAuthPaymentOrderDetails,
  createPaymentIntent,
  createUserPaymentIntent,
  authPay,
} from '../../services/paymentService';
import { getUserId } from '../../services/httpService';
import { formatMoney } from '../../utils/formatUtils';
import i18n from '../../locales/i18n';

function InlinePaymentFormWithElements({ eventId }: { eventId?: number }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: `${window.location.origin}/paid?type=payment&eventId=${eventId}` },
        redirect: 'if_required',
      } as any);
      if (result.error) {
        setError(result.error.message);
      }
    } catch (err: any) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
      <div className="w-full rounded-md bg-secondary-50">
        <PaymentElement />
      </div>

      {error && <div className="text-danger">{error}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="primary" type="submit" disabled={loading}>
          <Icon icon={FaCoins} /> {t('page.payment.buttons.pay')}
        </Button>
      </div>
    </form>
  );
}

export default function EventTicketPayment({
  veraId,
  onPaymentSuccess,
  eventId,
}: {
  veraId: string;
  onPaymentSuccess: () => void;
  eventId?: number;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<PaymentOrderDetailsDto | undefined>(undefined);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [isCreatingIntent, setIsCreatingIntent] = useState<boolean>(false);

  const stripeKey = process.env.REACT_APP_STRIPE_PK;
  const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

  const fetchDetails = (vId: string) => {
    getAuthPaymentOrderDetails(vId)
      .then((details) => {
        setData(details);
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    if (veraId) fetchDetails(veraId);
  }, [veraId]);

  useEffect(() => {
    if (!data) return;
    if (isCreatingIntent) return;
    if (!clientSecret) {
      createIntent('any');
    }
  }, [data]);

  useEffect(() => {
    if (clientSecret && !stripePromise && data) {
      authPay({ orderId: data.orderId, payerType: PayerType.USER })
        .then((response) => {
          if (response.paymentLink) window.location.href = response.paymentLink;
        })
        .catch((err) => console.error(err));
    }
  }, [clientSecret, stripePromise, data]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const redirectStatus = params.get('redirect_status');
      const paymentIntentClientSecret = params.get('payment_intent_client_secret');
      const paymentIntentId = params.get('payment_intent');

      if (redirectStatus === 'succeeded') {
        processSuccessfulPayment();
      } else if (redirectStatus === 'failed' || redirectStatus === 'canceled') {
        toast.error(t('page.payment.status.FAILED'));
        clearUrlParams();
      } else if (paymentIntentId && paymentIntentClientSecret && stripePromise) {
        stripePromise.then((stripe: any) => {
          if (!stripe) return;
          stripe
            .retrievePaymentIntent(paymentIntentClientSecret)
            .then(({ paymentIntent }: any) => {
              if (paymentIntent?.status === 'succeeded') {
                processSuccessfulPayment();
              } else if (
                paymentIntent?.status === 'requires_payment_method' ||
                paymentIntent?.status === 'canceled' ||
                paymentIntent?.status === 'requires_action' ||
                paymentIntent?.status === 'processing'
              ) {
                toast.error(t('page.payment.status.FAILED'));
                clearUrlParams();
              }
            })
            .catch((err: any) => {
              console.error(err);
            });
        });
      }
    } catch (err) {
      // ignore
    }
  }, [stripePromise, t, onPaymentSuccess]);

  const processSuccessfulPayment = () => {
    toast.success(t('page.common.success.message.payment'));
    clearUrlParams();
    setPaymentSucceeded(true);
    setShowPaymentForm(false);
    setClientSecret(null);
    try {
      onPaymentSuccess();
    } catch (err) {
      // ignore
    }
  };

  const clearUrlParams = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('redirect_status');
    url.searchParams.delete('payment_intent');
    url.searchParams.delete('payment_intent_client_secret');
    window.history.replaceState({}, document.title, url.toString());
  };

  const createIntent = (method: 'card' | 'ideal' | 'any') => {
    if (!data) return;

    setIsCreatingIntent(true);
    setClientSecret(null);

    const userId = getUserId();
    if (userId) {
      createPaymentIntent({ orderId: data.orderId, payerType: PayerType.USER, paymentMethod: method })
        .then((res) => {
          if (res && res.clientSecret) {
            setClientSecret(res.clientSecret);
            setShowPaymentForm(true);
            return Promise.resolve();
          }
          return createUserPaymentIntent(Math.round((data.amount || 0) * 100)).then((res2) => {
            if (res2 && res2.clientSecret) {
              setClientSecret(res2.clientSecret);
              setShowPaymentForm(true);
              return Promise.resolve();
            }
            return authPay({ orderId: data.orderId, payerType: PayerType.USER }).then((response) => {
              if (response.paymentLink) window.location.href = response.paymentLink;
              setShowPaymentForm(false);
            });
          });
        })
        .catch(() => {
          return createUserPaymentIntent(Math.round((data!.amount || 0) * 100))
            .then((res2) => {
              if (res2 && res2.clientSecret) {
                setClientSecret(res2.clientSecret);
                setShowPaymentForm(true);
                return Promise.resolve();
              }
              return authPay({ orderId: data!.orderId, payerType: PayerType.USER })
                .then((response) => {
                  if (response.paymentLink) window.location.href = response.paymentLink;
                })
                .catch((err2) => console.error(err2));
            })
            .catch(() => {
              return authPay({ orderId: data!.orderId, payerType: PayerType.USER })
                .then((response) => {
                  if (response.paymentLink) window.location.href = response.paymentLink;
                })
                .catch((err3) => console.error(err3));
            });
        })
        .finally(() => {
          setIsCreatingIntent(false);
        });
      return;
    }

    createPaymentIntent({ orderId: data.orderId, payerType: PayerType.USER, paymentMethod: method })
      .then((res) => {
        if (res && res.clientSecret) {
          setClientSecret(res.clientSecret);
          setShowPaymentForm(true);
          return Promise.resolve();
        }
        return authPay({ orderId: data.orderId, payerType: PayerType.USER }).then((response) => {
          if (response.paymentLink) window.location.href = response.paymentLink;
          setShowPaymentForm(false);
        });
      })
      .catch(() => {
        authPay({ orderId: data!.orderId, payerType: PayerType.USER })
          .then((response) => {
            if (response.paymentLink) window.location.href = response.paymentLink;
          })
          .catch((err) => console.error(err));
      })
      .finally(() => {
        setIsCreatingIntent(false);
      });
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-semibold text-primary-900">{t('page.payment.title')}</h3>
      </div>

      {paymentSucceeded && (
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="text-4xl text-success">
            <Icon icon={FaCheckCircle} />
          </div>
          <div className="text-lg font-semibold text-primary-900">{t('page.common.success.message.payment')}</div>
          <div>
            <Button
              variant="primary"
              onClick={() => {
                navigate('/');
              }}>
              {t('general.close')}
            </Button>
          </div>
        </div>
      )}

      {data && !paymentSucceeded && (
        <div className="space-y-1">
          <p className="text-secondary-600 text-sm">{data.description}</p>
          <p className="text-3xl font-semibold text-primary-900">{formatMoney(data.amount, i18n.language)}</p>
        </div>
      )}

      {isCreatingIntent && <p className="text-secondary-600 text-center text-sm">{t('page.payment.creating')}</p>}

      {showPaymentForm && !paymentSucceeded && clientSecret && stripePromise && (
        <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
          <InlinePaymentFormWithElements eventId={eventId} />
        </Elements>
      )}
    </div>
  );
}
